import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { productService } from '@/services/productService'
import type { ProductInsert, ProductUpdate } from '@/types/database'

export const PRODUCTS_QUERY_KEY = ['products']

export function useProducts() {
  return useQuery({
    queryKey: PRODUCTS_QUERY_KEY,
    queryFn: () => productService.getProducts(),
  })
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: [...PRODUCTS_QUERY_KEY, id],
    queryFn: () => productService.getProduct(id),
    enabled: !!id,
  })
}

export function useProductStats() {
  return useQuery({
    queryKey: [...PRODUCTS_QUERY_KEY, 'stats'],
    queryFn: () => productService.getProductStats(),
  })
}

export function useCreateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (product: Omit<ProductInsert, 'organization_id'>) =>
      productService.createProduct(product),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY })
    },
  })
}

export function useUpdateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: ProductUpdate }) =>
      productService.updateProduct(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: [...PRODUCTS_QUERY_KEY, id] })
    },
  })
}

export function useDeleteProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => productService.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY })
    },
  })
}

export function useDuplicateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      sourceProductId: string
      newName: string
      newSku?: string
      copyDocuments?: boolean
    }) =>
      productService.duplicateProduct(params.sourceProductId, {
        newName: params.newName,
        newSku: params.newSku,
        copyDocuments: params.copyDocuments,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY })
    },
  })
}
